import React, { useEffect, useState, forwardRef } from "react";
import { Box, FormHelperText, CircularProgress, useTheme, OutlinedInput, TextField } from "@mui/material";
import PhoneInput from "react-phone-number-input/input";
import "react-phone-number-input/style.css";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useAuth } from "../../hooks/useAuth";
import Question from "../../components/Question";
import ContinueButton from "../../components/ContinueButton";
import { continueButtonSx, phoneInputSx, textFieldSx } from "../../lib/uiformatting";
import { reset, setReturning } from "../../redux/userSlice";
import MoveButton from "../../components/MoveButton";

const CustomMuiOutlinedInput = forwardRef((props, ref) => (
    <OutlinedInput
        {...props}
        inputRef={ref}
        label=""
        notched={false}
        sx={phoneInputSx}
    />
));

export default function AppointmentBooking() {
    const theme = useTheme();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [firstEnter, setFirstEnter] = useState(true);
    const [phoneNo, setPhoneNo] = useState("");

    const {
        user,
        handleChange,
        phoneNoValid,
        sendOtp,
        showOtpInput,
        isSendingOtp,
        isVerifyOtpErr,
        setShowOtpInput,
    } = useAuth({
        onSuccess: () => {
            navigate("/appointment-booking-patients");
        }
    });

    useEffect(() => {
        dispatch(reset());
        dispatch(setReturning(true));
        setPhoneNo('');
    }, [dispatch]);

    const handlePhoneChange = (value) => {
        setPhoneNo(value || '');
        handleChange("phoneNo", value);
    };

    return (
        <Question
            title="Welcome back! Let's schedule your follow-up"
            subtitle="Please enter your mobile number. We'll send a quick confirmation code."
            noBack
            input={
                <Box sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    backgroundColor: theme.palette.background.default
                }}>
                    <Box sx={{ width: "100%", maxWidth: "600px" }}>
                        <PhoneInput
                            country="US"
                            placeholder="(234) 567-8901"
                            value={phoneNo}
                            onChange={handlePhoneChange}
                            onBlur={() => setFirstEnter(false)}
                            inputComponent={CustomMuiOutlinedInput}
                        />
                        <FormHelperText error={!phoneNoValid && !firstEnter} sx={{ ml: 0, mt: 0.5 }}>
                            {!phoneNoValid && !firstEnter && "Please enter a valid 10-digit US phone number"}
                        </FormHelperText>

                        {showOtpInput ? (
                            <>
                                <TextField
                                    placeholder="Enter code"
                                    type="tel"
                                    inputMode="decimal"
                                    sx={{ ...textFieldSx, width: "100%", mt: 2 }}
                                    autoComplete="off"
                                    value={user?.otpCode}
                                    onChange={(e) => handleChange("otpCode", e.target.value)}
                                    disabled={user?.otpCode?.length >= 6}
                                />
                                {isVerifyOtpErr && (
                                    <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                                        <Box sx={{ width: "200px" }}>
                                            <MoveButton
                                                title="Try Again"
                                                onClick={() => {
                                                    setPhoneNo('');
                                                    setShowOtpInput(false);
                                                    navigate('/appointment-booking');
                                                }} sx={{ justifyContent: "center" }}
                                            />
                                        </Box>
                                    </Box>
                                )}
                            </>
                        ) : (
                            <Box sx={{ ...continueButtonSx, width: "100%", mt: 4 }}>
                                <ContinueButton
                                    title={isSendingOtp ? <CircularProgress color="secondary" /> : "Get Code"}
                                    disabled={!phoneNoValid || !phoneNo}
                                    onClick={sendOtp}
                                    selected={phoneNoValid && phoneNo}
                                />
                            </Box>
                        )}
                    </Box>
                </Box>
            }
        />
    );
}
